import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AddClassForm from "../components/AddClassForm";
import AllClassesTable from "../components/AllClassesTable";
import UserClassesCardView from "../components/UserClassesCardView";
import Attendance from "../components/Attendance";
import themestyle from "../theme";
import { CLASS_PAGE_TEXT ,CLASS_MESSAGES} from "../utils/const";
import { useMutation } from "@apollo/client/react";
import { DELETE_CLASS } from "../graphql/mutation";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ConfirmDialog from "../components/confirmdialogbox";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CustomSnackbar from "../components/Snackbar";
import { useQuery } from "@apollo/client";
import { ME } from "../graphql/query";

type ViewType = "ALL_CLASSES" | "ADD_CLASS" | "VIEW_CLASS" | "EDIT_CLASS" | "ATTENDANCE";

const ClassManagement: React.FC = () => {
  const navigate = useNavigate();
  const { mode, id } = useParams<{ mode?: string; id?: string }>();
  const location = useLocation();
  const [refreshCount, setRefreshCount] = useState(0);

  const getActiveView = (): ViewType => {
    if (location.pathname === "/classes/add") return "ADD_CLASS";
    if (mode === "view" && id) return "VIEW_CLASS";
    if (mode === "edit" && id) return "EDIT_CLASS";
    if (mode === "attendance" && id) return "ATTENDANCE";
    return "ALL_CLASSES";
  };

  const activeView = getActiveView();
  const selectedClassId = id;

  const { data } = useQuery(ME, {
    fetchPolicy: "cache-first",
  });

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();
  const userRole = data?.me?.role?.toUpperCase() || storedUser?.role?.toUpperCase() || "";

  useEffect(() => {
    document.title = CLASS_PAGE_TEXT.PAGE_TITLE;
  }, []);

  const handleView = (id: string) => {
    navigate(`/classes/view/${id}`, { state: { from: location.pathname } });
  };

  const handleEdit = (id?: string) => {
    const targetId = id || selectedClassId;
    if (targetId) navigate(`/classes/edit/${targetId}`, { state: { from: location.pathname } });
  };

  const handleAttendance = (id: string, date?: string) => {
    navigate(`/classes/attendance/${id}`, { state: { from: location.pathname, sessionDate: date } });
  };

  const handleBack = () => {
    const from = (location.state as any)?.from;
    navigate(from || "/classes");
  };

  const handleSuccess = (isDelete: boolean = false) => {
    const from = (location.state as any)?.from;
    const isManagementView = activeView === "EDIT_CLASS" ||
                            activeView === "VIEW_CLASS" ||
                            activeView === "ADD_CLASS" ||
                            activeView === "ATTENDANCE";
    if (isManagementView) {
      navigate(from || "/classes");
    } else {
    }
    setRefreshCount((prev) => prev + 1);
  };

  const [deleteClass] = useMutation(DELETE_CLASS);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error" | "info">("success");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState<{ id: string; name: string; date?: string } | null>(null);
  
  const handleDeleteClick = (id: string, name: string, date?: string) => {
    setClassToDelete({ id, name, date });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!classToDelete) return;
    try {
      const { data: response }: any = await deleteClass({
        variables: { 
          id: classToDelete.id,
          date: classToDelete.date 
        },
      });
      if (response.deleteClass.success) {
        setSnackbarMessage(
          classToDelete.date 
            ? `${classToDelete.name} class deleted successfully` 
            : CLASS_MESSAGES.DELETE_SUCCESS
        );
        setSnackbarType("error");
        setSnackbarOpen(true);
        handleSuccess(true);
      } else {
        setSnackbarMessage(response.deleteClass.message);
        setSnackbarType("error");
        setSnackbarOpen(true);
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setDeleteDialogOpen(false);
      setClassToDelete(null);
    }
  };

  const isAllClassesActive =
    activeView === "ALL_CLASSES";


  return (
    <Box
      sx={{
        mt: { xs: 1, sm: 3, md: 3 },
        px: { xs: 1, md: 3 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: { xs: 2, md: 0 },
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
          {(activeView === "VIEW_CLASS" || activeView === "EDIT_CLASS" || activeView === "ATTENDANCE") && (
            <Box
              onClick={handleBack}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                color: "#6B7280",
                fontSize: "14px",
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />
              {CLASS_PAGE_TEXT.BACK}
            </Box>
          )}

          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ fontSize: "24px" }}
            fontFamily={themestyle.fontFamily.Roboto}
          >
            {activeView === "EDIT_CLASS"
              ? CLASS_PAGE_TEXT.HEADER.EDIT
              : activeView === "VIEW_CLASS"
              ? CLASS_PAGE_TEXT.HEADER.VIEW
              : activeView === "ATTENDANCE"
              ? "Class Attendance"
              : CLASS_PAGE_TEXT.HEADER.DEFAULT}
          </Typography>
        </Box>
        {userRole !== "INSTRUCTOR" && userRole !== "USER" && (activeView === "ALL_CLASSES" || activeView === "ADD_CLASS") && (
          <Box
            sx={{
              display: "flex",
              backgroundColor: themestyle.colors.white,
              borderRadius: "6px",
              padding: "6px",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
              width: { xs: "90%", md: "fit-content" },
              justifyContent: { xs: "space-between", md: "flex-start" },
            }}
          >
            <Box
              onClick={() => {
                navigate("/classes");
              }}
              sx={{
                cursor: "pointer",
                flex: 1,
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                py: { xs: 1.5, sm: 1.5 },
                fontSize: { xs: "14px", sm: "16px" },
                borderRadius: "6px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: isAllClassesActive ? "#4CAF50" : "#6B7280",
                backgroundColor: isAllClassesActive ? "#EEF7E9" : "transparent",
                transition: "all 0.3s ease",
              }}
            >
            {CLASS_PAGE_TEXT.TABS.ALL}
            </Box>
            <Box
              onClick={() => {
                navigate("/classes/add");
              }}
              sx={{
                cursor: "pointer",
                opacity: 1,
                flex: 1,
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                py: { xs: 1.5, sm: 1.5 },
                fontSize: { xs: "14px", sm: "16px" },
                borderRadius: "6px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: activeView === "ADD_CLASS" ? "#4CAF50" : "#6B7280",
                backgroundColor:
                  activeView === "ADD_CLASS" ? "#EEF7E9" : "transparent",
                transition: "all 0.3s ease",
              }}
            >
            {CLASS_PAGE_TEXT.TABS.ADD}
            </Box>
          </Box>
        )}

      </Box>
      {activeView === "ADD_CLASS" && (
        <AddClassForm
          mode="add"
          onSuccess={handleSuccess}
          onCancel={handleBack}
        />
      )}
      {activeView === "VIEW_CLASS" && (
        <AddClassForm
          mode="view"
          id={selectedClassId}
          onCancel={handleBack}
          onEdit={() => handleEdit(selectedClassId)}
        />
      )}
      {activeView === "EDIT_CLASS" && (
        <AddClassForm
          mode="edit"
          id={selectedClassId}
          onSuccess={handleSuccess}
          onCancel={() => navigate(`/classes/view/${selectedClassId}`)}
          onDelete={(id, name) => {
            handleDeleteClick(id, name);
          }}
        />
      )}
      {activeView === "ATTENDANCE" && (
        <Attendance
          classId={selectedClassId}
          sessionDate={(location.state as any)?.sessionDate}
          onBack={handleBack}
        />
      )}
      {activeView === "ALL_CLASSES" && (
        userRole === "USER" ? (
          <UserClassesCardView 
            onView={handleView}
            refreshTrigger={refreshCount}
            initialOpenClass={(location.state as any)?.openClass}
          />
        ) : (
          <AllClassesTable
            onView={handleView}
            onEdit={handleEdit}
            onAttendance={handleAttendance}
            onDelete={handleDeleteClick}
            refreshTrigger={refreshCount}
          />
        )
      )}
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
      <ConfirmDialog
        open={deleteDialogOpen}
        icon={<DeleteOutlineIcon sx={{ color: themestyle.colors.error }} />}
        message={classToDelete ? `Are you sure you want to delete class "${classToDelete.name}"?` : ""}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteDialogOpen(false)}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="danger"
        iconBgColor="danger"
      />
    </Box>
  );
};
export default ClassManagement;
